import React, { useMemo, useState, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import ConfirmModal from './ConfirmModal';
import { sanitizeNumeric } from 'utils/number';
import styles from './ManualRateWithdrawModal.module.scss';

interface ManualRateWithdrawModalProps {
  open: boolean;
  username: string;
  /** USD amount the user asked to withdraw. */
  amount: number | string;
  cryptoCode: string | null;
  address: string | null;
  onClose: () => void;
  onConfirm: (receiveAmount: string) => void;
}

const MAX_DECIMALS = 8;

/** Truncates the decimal part to at most `maxDecimals` digits without rounding. */
const clampDecimals = (value: string, maxDecimals: number): string => {
  const [whole, decimals] = value.split('.');
  if (decimals === undefined) return whole;
  return `${whole}.${decimals.slice(0, maxDecimals)}`;
};

const ManualRateWithdrawModal: React.FC<ManualRateWithdrawModalProps> = ({
  open,
  username,
  amount,
  cryptoCode,
  address,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = useState('');

  // Reset the input whenever the modal is reopened for a different row.
  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  const numericValue = Number(value);
  const isValid = value.trim() !== '' && Number.isFinite(numericValue) && numericValue > 0;

  const impliedRate = useMemo(() => {
    if (!isValid) return null;
    const usdAmount = Number(amount);
    if (!Number.isFinite(usdAmount)) return null;
    return usdAmount / numericValue;
  }, [isValid, numericValue, amount]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = clampDecimals(sanitizeNumeric(e.target.value, true), MAX_DECIMALS);
    setValue(sanitized);
  };

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm(value);
  };

  const code = cryptoCode || 'crypto';
  const formattedAmount = `$ ${Number(amount).toLocaleString()}`;

  return (
    <ConfirmModal
      open={open}
      title="Confirm Withdrawal — manual rate"
      description={
        <>
          User <strong>{username}</strong> is withdrawing {formattedAmount}. The exchange rate was
          unavailable, so enter the amount of {code} to send to their wallet.
        </>
      }
      confirmText="Confirm"
      confirmDisabled={!isValid}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <Box className={styles.body}>
        {address && (
          <Box className={styles.addressRow}>
            <Typography className={styles.label}>Destination address</Typography>
            <Typography className={styles.address}>{address}</Typography>
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          label={`Amount (${code})`}
          value={value}
          onChange={handleChange}
          className={styles.input}
          autoComplete="off"
          placeholder="0.00000000"
        />

        {impliedRate !== null && (
          <Typography className={styles.impliedRate}>
            Implied rate: 1 {code} = ${' '}
            {impliedRate.toLocaleString(undefined, { maximumFractionDigits: 8 })}
          </Typography>
        )}
      </Box>
    </ConfirmModal>
  );
};

export default ManualRateWithdrawModal;
